import { useEffect, useState } from 'react';
import { callApi } from '../api';
import { Section, ErrorBox, LoadingSpinner, EmptyState } from '../components/Section';
import { UserChip } from '../components/UserChip';
import { WindowChips, windowLabel } from '../components/WindowChips';
import { useDebounced } from '../hooks/useDebounced';
import { useSession } from '../hooks/useSession';
import { formatDateTime } from '../utils';

export function Search() {
  const { session, excludeAdmins } = useSession();
  const [queryDays, setQueryDays] = useState(7);
  const [userDays, setUserDays] = useState(30);
  const [slow, setSlow] = useState(null);
  const [zero, setZero] = useState(null);
  const [byUser, setByUser] = useState(null);
  const [error, setError] = useState('');

  const adminQs = `&exclude_admins=${excludeAdmins}`;

  useEffect(() => {
    Promise.all([
      callApi(`/api/v1/admin/search/slow?days=${queryDays}&limit=30${adminQs}`, { accessToken: session.access_token }),
      callApi(`/api/v1/admin/search/zero-results?days=${queryDays}&limit=30${adminQs}`, { accessToken: session.access_token }),
    ])
      .then(([s, z]) => { setSlow(s); setZero(z); })
      .catch((e) => setError(e.message));
  }, [queryDays, adminQs, session.access_token]);

  useEffect(() => {
    callApi(`/api/v1/admin/search/by-user?days=${userDays}&limit=30${adminQs}`, {
      accessToken: session.access_token,
    })
      .then(setByUser)
      .catch((e) => setError(e.message));
  }, [userDays, adminQs, session.access_token]);

  if (error && !slow && !zero && !byUser) return <ErrorBox message={error} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-stone-800">Search analytics</h1>
        <WindowChips value={queryDays} onChange={setQueryDays} label="queries" />
      </div>

      {excludeAdmins && (
        <div className="text-xs text-stone-500 italic">
          Excluding admin users (toggle in sidebar).
        </div>
      )}

      <ErrorBox message={error} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title={`Slowest queries (${windowLabel(queryDays)})`}>
          {slow ? (
            <QueryTable
              rows={slow.rows}
              valueLabel="avg ms"
              valueFn={(r) => Math.round(r.avg_execution_time_ms)}
            />
          ) : <LoadingSpinner />}
        </Section>
        <Section title={`Zero-result queries (${windowLabel(queryDays)})`}>
          {zero ? (
            <QueryTable
              rows={zero.rows}
              valueLabel="count"
              valueFn={(r) => r.count}
            />
          ) : <LoadingSpinner />}
        </Section>
      </div>

      <Section
        title="Top users by query count"
        action={<WindowChips value={userDays} onChange={setUserDays} />}
      >
        {byUser ? (
          byUser.items.length === 0 ? (
            <p className="text-sm text-stone-500">No queries.</p>
          ) : (
            <div className="divide-y">
              {byUser.items.map((u) => (
                <div key={u.user_id} className="flex justify-between items-center py-1.5 text-sm">
                  <UserChip userId={u.user_id} displayName={u.display_name} />
                  <span className="text-xs text-stone-500">
                    {u.query_count} queries · last {formatDateTime(u.latest_query_at)}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : <LoadingSpinner />}
      </Section>

      <RecentSearches excludeAdmins={excludeAdmins} />
    </div>
  );
}

function RecentSearches({ excludeAdmins }) {
  const { session } = useSession();
  const [days, setDays] = useState(7);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const buildUrl = (cur) => {
    const params = new URLSearchParams({
      days: String(days),
      limit: '50',
      exclude_admins: String(excludeAdmins),
    });
    if (debouncedQ) params.set('q', debouncedQ);
    if (cur) params.set('cursor', cur);
    return `/api/v1/admin/search/recent?${params.toString()}`;
  };

  useEffect(() => {
    setLoading(true);
    callApi(buildUrl(null), { accessToken: session.access_token })
      .then((d) => {
        setItems(d.items);
        setHasMore(d.has_more);
        setCursor(d.next_cursor);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, debouncedQ, excludeAdmins, session.access_token]);

  const loadMore = async () => {
    try {
      const d = await callApi(buildUrl(cursor), { accessToken: session.access_token });
      setItems((prev) => [...prev, ...d.items]);
      setHasMore(d.has_more);
      setCursor(d.next_cursor);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Section
      title={`Recent searches (${windowLabel(days)})`}
      action={
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="filter by text…"
            className="text-xs border rounded px-2 py-1 w-44"
          />
          <WindowChips value={days} onChange={setDays} />
        </div>
      }
    >
      <ErrorBox message={error} />
      {loading ? <LoadingSpinner /> : (
        items.length === 0 ? <EmptyState>No searches in this view.</EmptyState> : (
          <div className="divide-y">
            {items.map((s) => (
              <div key={s.id} className="flex items-start justify-between py-1.5 text-sm gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs truncate">
                    {s.raw_query || <span className="text-stone-400">— (filter only)</span>}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    {s.user_id ? (
                      <UserChip userId={s.user_id} displayName={s.user_display_name} />
                    ) : (
                      <span className="text-stone-400">anonymous</span>
                    )}
                    <span>·</span>
                    <span>{s.result_count ?? '—'} results</span>
                    {s.execution_time_ms != null && (
                      <>
                        <span>·</span>
                        <span>{s.execution_time_ms}ms</span>
                      </>
                    )}
                  </div>
                </div>
                <span className="text-xs text-stone-500 whitespace-nowrap">
                  {formatDateTime(s.created_at)}
                </span>
              </div>
            ))}
          </div>
        )
      )}
      {hasMore && (
        <button
          onClick={loadMore}
          className="text-sm text-stone-700 underline mt-2"
        >
          Load more
        </button>
      )}
    </Section>
  );
}

function QueryTable({ rows, valueLabel, valueFn }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-stone-500">Nothing here.</p>;
  }
  return (
    <div className="space-y-1 text-sm">
      <div className="flex justify-between text-xs text-stone-500 border-b pb-1">
        <span>query</span>
        <span>{valueLabel}</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="flex justify-between gap-2">
          <span className="font-mono text-xs truncate max-w-[60%]">{r.raw_query}</span>
          <span className="text-stone-700 whitespace-nowrap">{valueFn(r)}</span>
        </div>
      ))}
    </div>
  );
}
